/**
 * StudentDetail — EntityDetailShell-based detail page.
 *
 * Cross-feature composition (entitlements, billing, bookings tabs) is
 * injected via render-props so People does not import sibling features.
 *
 * Edit pre-load: the "Edit" button triggers a fresh GET /students/:id
 * (via useStudent with staleTime: 0) so the modal never opens with stale data.
 *
 * Image upload: photo_url is persisted via PATCH immediately after the
 * Firebase Storage upload completes.
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle } from '@/components/common/Card';
import { PageContainer } from '@/components/layout';
import { EntityDetailShell } from '@/components/entity';
import {
  useStudent,
  useUpdateStudent,
} from '@features/people/api/students';
import { StudentFormModal } from '@features/people/components/StudentFormModal';
import { StudentDetailSidebar } from '@features/people/components/StudentDetailSidebar';
import { buildStudentDetailConfig } from '@features/people/config/studentsEntityConfig';
import { useAuth } from '@/context';

interface StudentDetailProps {
  renderEntitlementsTab?: (studentId: string) => React.ReactNode;
  renderBillingTab?: (studentId: string) => React.ReactNode;
  renderBookingsTab?: (studentId: string) => React.ReactNode;
  renderAttendanceTab?: (studentId: string) => React.ReactNode;
  renderCalendarTab?: (studentId: string) => React.ReactNode;
  renderGradesTab?: (studentId: string) => React.ReactNode;
  /**
   * 11F.7 — left-rail Insight cells that need cross-feature data (total
   * spend from Billing, attendance % from Attendance). Injected at the
   * route layer for the same reason the tabs are: People may not import a
   * sibling feature.
   */
  renderInsights?: (studentId: string) => React.ReactNode;
  /** 11F.7 — Overview body: invoice summary + recent invoices (Billing). */
  renderOverviewBilling?: (studentId: string) => React.ReactNode;
}

export const StudentDetail: React.FC<StudentDetailProps> = ({
  renderEntitlementsTab,
  renderBillingTab,
  renderBookingsTab,
  renderAttendanceTab,
  renderCalendarTab,
  renderGradesTab,
  renderInsights,
  renderOverviewBilling,
}) => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const [isEditOpen, setIsEditOpen] = useState(false);

  // staleTime: 0 ensures we always re-fetch when the detail page mounts
  const { data: student, isLoading, isError } = useStudent(id ?? '');
  const updateStudent = useUpdateStudent();

  const handlePhotoUpload = async (url: string) => {
    if (!student) return;
    await updateStudent.mutateAsync({ id: student.id, body: { photo_url: url } });
  };

  if (isLoading) {
    return (
      <PageContainer title="Loading">
        <Card padding="lg"><p style={{ color: '#64748b' }}>Loading student…</p></Card>
      </PageContainer>
    );
  }

  if (isError || !student) {
    return (
      <PageContainer title="Student Not Found">
        <Card padding="lg">
          <p>The student you are looking for does not exist.</p>
        </Card>
      </PageContainer>
    );
  }

  // Overview tab body (11F.7). Identity/contact fields moved to the left
  // rail — repeating them here was the old page's main redundancy. What
  // remains is what an admin opens a student to see: enrolment state and
  // money.
  const overviewContent = (
    <div data-testid="candeza-student-overview">
      <CardHeader style={{ paddingLeft: 0 }}>
        <CardTitle>Enrollments</CardTitle>
      </CardHeader>
      {renderEntitlementsTab ? (
        renderEntitlementsTab(student.id)
      ) : (
        <p style={{ color: '#64748b', fontSize: 14 }}>
          Enrollment data is not available on this build.
        </p>
      )}

      {renderOverviewBilling && (
        <div style={{ marginTop: 24 }} data-testid="candeza-student-overview-billing">
          {renderOverviewBilling(student.id)}
        </div>
      )}
    </div>
  );

  const detailConfig = buildStudentDetailConfig({
    renderOverviewTab: () => overviewContent,
    renderEntitlementsTab,
    renderBillingTab,
    renderBookingsTab,
    renderAttendanceTab,
    renderCalendarTab,
    renderGradesTab,
    renderSidebar: (s) => (
      <StudentDetailSidebar student={s} renderInsights={renderInsights} />
    ),
  });

  return (
    <>
      <EntityDetailShell
        entity={student}
        config={detailConfig}
        tenantId={tenantId ?? undefined}
        onEdit={() => setIsEditOpen(true)}
        onPhotoUpload={handlePhotoUpload}
        canEdit
      />

      <StudentFormModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        student={student}
      />
    </>
  );
};
